import React from "react";
import { connect } from "react-redux";
import { Button, FormControl, Modal, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import store from "../../../store";
import { GetQuotesList, RemoveQuote } from "../../../actions/quote/quote-actions";
import { TO_QUOTE_CREATE } from "../../../helpers/constants";
import "./quote-search.scss";

const searchableFields = [
    "QuoteNo",
    "CustomerName",
    "CustomerEmail",
    "ShipCompanyName",
    "ShipCountry",
];

const formatDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Not available" : date.toLocaleDateString();
};

class QuoteList extends React.Component {
    state = {
        query: "",
        isLoading: true,
        loadError: "",
        pendingDelete: null,
        isDeleting: false,
        deleteError: "",
    };

    componentDidMount() {
        this.loadQuotes();
    }

    loadQuotes = async () => {
        this.setState({ isLoading: true, loadError: "" });
        try {
            const result = await store.dispatch(GetQuotesList());
            this.setState({
                isLoading: false,
                loadError: result && result.status
                    ? ""
                    : "We couldn't load your quotes. Check your connection and try again.",
            });
        } catch (error) {
            this.setState({
                isLoading: false,
                loadError: "We couldn't load your quotes. Check your connection and try again.",
            });
        }
    };

    handleSearch = (event) => {
        this.setState({ query: event.target.value });
    };

    clearSearch = () => {
        this.setState({ query: "" });
    };

    openDeleteConfirmation = (quote) => {
        this.setState({ pendingDelete: quote, deleteError: "" });
    };

    closeDeleteConfirmation = () => {
        if (!this.state.isDeleting) {
            this.setState({ pendingDelete: null, deleteError: "" });
        }
    };

    confirmDelete = async () => {
        const { pendingDelete } = this.state;
        if (!pendingDelete || this.state.isDeleting) return;

        this.setState({ isDeleting: true, deleteError: "" });
        try {
            const result = await store.dispatch(RemoveQuote(pendingDelete.QuoteNo));
            if (result && result.status) {
                this.setState({ pendingDelete: null, isDeleting: false });
            } else {
                this.setState({
                    isDeleting: false,
                    deleteError: result && result.msg
                        ? result.msg
                        : "We couldn't delete this quote. Try again.",
                });
            }
        } catch (error) {
            this.setState({
                isDeleting: false,
                deleteError: "We couldn't delete this quote. Check your connection and try again.",
            });
        }
    };

    getFilteredQuotes = () => {
        const quotes = Array.isArray(this.props.quotes) ? this.props.quotes : [];
        const query = this.state.query.trim().toLowerCase();
        if (!query) return quotes;

        return quotes.filter((quote) => searchableFields.some((field) =>
            String(quote[field] || "").toLowerCase().includes(query)
        ));
    };

    renderTableRows = (quotes) => quotes.map((quote) => (
        <tr key={quote.QuoteNo}>
            <td data-label="Quote"><strong>#{quote.QuoteNo}</strong></td>
            <td data-label="Customer">{quote.CustomerName || "Not provided"}</td>
            <td data-label="Email"><span className="quote-email">{quote.CustomerEmail || "Not provided"}</span></td>
            <td data-label="Created">{formatDate(quote.QuoteDate)}</td>
            <td data-label="Ship to company">{quote.ShipCompanyName || "Not provided"}</td>
            <td data-label="Country">{quote.ShipCountry || "Not provided"}</td>
            <td data-label="Actions" className="quote-row-actions">
                <Link className="quote-action-button edit" to={`${TO_QUOTE_CREATE}/${quote.QuoteNo}`} aria-label={`Edit quote ${quote.QuoteNo}`}>
                    <i className="fa fa-pencil" aria-hidden="true"></i>
                    <span>Edit</span>
                </Link>
                <Button variant="link" className="quote-action-button delete" onClick={() => this.openDeleteConfirmation(quote)} aria-label={`Delete quote ${quote.QuoteNo}`}>
                    <i className="fa fa-trash" aria-hidden="true"></i>
                    <span>Delete</span>
                </Button>
            </td>
        </tr>
    ));

    render() {
        const quotes = this.getFilteredQuotes();
        const totalQuotes = Array.isArray(this.props.quotes) ? this.props.quotes.length : 0;
        const { query, isLoading, loadError, pendingDelete, isDeleting, deleteError } = this.state;

        return (
            <main className="quote-search-page">
                <div className="quote-search-shell">
                    <header className="quote-search-header">
                        <div>
                            <span className="page-eyebrow">External quotes</span>
                            <h1>Quotes</h1>
                            <p>Find, review, and update quote requests submitted to IntrepidCS.</p>
                        </div>
                        <Link className="create-quote-button" to={TO_QUOTE_CREATE}>
                            <i className="fa fa-plus" aria-hidden="true"></i>
                            <span>Create quote</span>
                        </Link>
                    </header>

                    <section className="quote-results" aria-labelledby="quote-results-heading">
                        <div className="quote-search-toolbar">
                            <div>
                                <h2 id="quote-results-heading">Your quotes</h2>
                                <p>{isLoading ? "Loading quotes..." : `${quotes.length} of ${totalQuotes} quotes`}</p>
                            </div>
                            <div className="quote-search-control">
                                <label htmlFor="quote-search">Search quotes</label>
                                <div className="search-input-wrap">
                                    <i className="fa fa-search" aria-hidden="true"></i>
                                    <FormControl
                                        id="quote-search"
                                        type="search"
                                        value={query}
                                        onChange={this.handleSearch}
                                        placeholder="Quote number, customer, email, company, or country"
                                    />
                                    {query && (
                                        <Button variant="link" className="clear-search" onClick={this.clearSearch} aria-label="Clear search">
                                            <i className="fa fa-times" aria-hidden="true"></i>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {loadError && (
                            <div className="quote-list-state error" role="alert">
                                <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                                <h3>Quotes couldn't load</h3>
                                <p>{loadError}</p>
                                <Button variant="primary" onClick={this.loadQuotes}>Try again</Button>
                            </div>
                        )}

                        {!loadError && isLoading && (
                            <div className="quote-list-loading" role="status" aria-live="polite">
                                {[1, 2, 3, 4].map((row) => <span key={row}></span>)}
                                <p>Loading your quotes...</p>
                            </div>
                        )}

                        {!loadError && !isLoading && quotes.length === 0 && (
                            <div className="quote-list-state">
                                <i className={`fa ${query ? "fa-search" : "fa-file-text"}`} aria-hidden="true"></i>
                                <h3>{query ? "No quotes match your search" : "No quotes yet"}</h3>
                                <p>{query ? "Try a quote number, customer, email, company, or country." : "Create your first external quote to get started."}</p>
                                {query
                                    ? <Button variant="outline-primary" onClick={this.clearSearch}>Clear search</Button>
                                    : <Link className="btn btn-primary" to={TO_QUOTE_CREATE}>Create quote</Link>}
                            </div>
                        )}

                        {!loadError && !isLoading && quotes.length > 0 && (
                            <div className="quote-table-wrap">
                                <Table className="quote-search-table">
                                    <thead>
                                        <tr>
                                            <th>Quote</th>
                                            <th>Customer</th>
                                            <th>Email</th>
                                            <th>Created</th>
                                            <th>Ship to company</th>
                                            <th>Country</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>{this.renderTableRows(quotes)}</tbody>
                                </Table>
                            </div>
                        )}
                    </section>
                </div>

                {pendingDelete && <Modal show onHide={this.closeDeleteConfirmation} centered dialogClassName="delete-quote-modal">
                    <Modal.Header>
                        <Modal.Title>Delete quote #{pendingDelete && pendingDelete.QuoteNo}?</Modal.Title>
                        <Button variant="link" className="modal-close-button" onClick={this.closeDeleteConfirmation} disabled={isDeleting} aria-label="Close">
                            <i className="fa fa-times" aria-hidden="true"></i>
                        </Button>
                    </Modal.Header>
                    <Modal.Body>
                        <p>This permanently removes the quote for <strong>{pendingDelete && (pendingDelete.CustomerName || "this customer")}</strong>.</p>
                        <p>This action cannot be undone.</p>
                        {deleteError && <div className="delete-error" role="alert">{deleteError}</div>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={this.closeDeleteConfirmation} disabled={isDeleting}>Keep quote</Button>
                        <Button variant="danger" onClick={this.confirmDelete} disabled={isDeleting}>
                            {isDeleting ? "Deleting..." : "Delete quote"}
                        </Button>
                    </Modal.Footer>
                </Modal>}
            </main>
        );
    }
}

const stateMap = (state) => ({
    quotes: state.user.quotes,
});

export default connect(stateMap)(QuoteList);
